import React from 'react';
import {
  Paper,
  Box,
  Typography,
  Avatar,
  Skeleton,
  Chip
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { formatCurrency } from '../../utils/dashboardHelpers';

/**
 * Ranking de Top 5 Trabajadores
 * Muestra los trabajadores con mejor desempeño en el periodo
 */
const TopTrabajadoresRanking = ({ data, currency = null, loading = false }) => {
  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          borderRadius: 2,
          height: 400
        }}
      >
        <Skeleton variant="text" width="60%" height={40} />
        {[1, 2, 3, 4, 5].map((i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
            <Skeleton variant="circular" width={50} height={50} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="70%" />
              <Skeleton variant="text" width="50%" />
            </Box>
          </Box>
        ))}
      </Paper>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          borderRadius: 2,
          height: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography color="text.secondary">No hay datos disponibles</Typography>
      </Paper>
    );
  }

  // Colores para las medallas
  const getMedalColor = (position) => {
    switch (position) {
      case 1:
        return '#FFD700'; // Oro
      case 2:
        return '#C0C0C0'; // Plata
      case 3:
        return '#CD7F32'; // Bronce
      default:
        return '#9E9E9E'; // Gris
    }
  };

  const getMedalIcon = (position) => {
    if (position <= 3) {
      return <EmojiEventsIcon sx={{ fontSize: '1.5rem', color: getMedalColor(position) }} />;
    }
    return (
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: '#E0E0E0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.875rem',
          fontWeight: 600,
          color: '#666'
        }}
      >
        {position}
      </Box>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
        border: '1px solid rgba(0, 0, 0, 0.08)',
        borderRadius: 2,
        height: 400
      }}
    >
      {/* Header */}
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: '#1A237E',
          fontSize: '1.125rem',
          mb: 3
        }}
      >
        ⭐ Top 5 Trabajadores
      </Typography>

      {/* Lista de trabajadores */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {data.map((trabajador, index) => {
          const position = index + 1;
          const isTop3 = position <= 3;

          return (
            <Box
              key={trabajador.id_trabajador}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 1.5,
                borderRadius: 2,
                bgcolor: isTop3 ? 'rgba(26, 35, 126, 0.03)' : 'transparent',
                border: isTop3 ? '1px solid rgba(26, 35, 126, 0.1)' : '1px solid transparent',
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: 'rgba(26, 35, 126, 0.05)',
                  transform: 'translateX(4px)',
                  border: '1px solid rgba(26, 35, 126, 0.15)'
                }
              }}
            >
              {/* Posición/Medalla */}
              <Box sx={{ minWidth: 40, display: 'flex', justifyContent: 'center' }}>
                {getMedalIcon(position)}
              </Box>

              {/* Avatar */}
              <Avatar
                src={trabajador.foto}
                alt={trabajador.nombre_completo || 'Trabajador'}
                sx={{
                  width: 50,
                  height: 50,
                  bgcolor: isTop3 ? '#1A237E' : '#9E9E9E',
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  border: isTop3 ? '2px solid #FFD700' : 'none'
                }}
              >
                {(trabajador.nombre_completo?.charAt(0) || 'T').toUpperCase()}
              </Avatar>

              {/* Info del trabajador */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: '#1A237E',
                    fontSize: '0.875rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {trabajador.nombre_completo || 'Sin nombre'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Chip
                    label={`${trabajador.cantidad_servicios} servicios`}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      bgcolor: '#E3F2FD',
                      color: '#1976D2',
                      fontWeight: 500
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#4CAF50',
                      fontWeight: 600,
                      fontSize: '0.75rem'
                    }}
                  >
                    {formatCurrency(trabajador.comisiones_totales, currency)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Footer con total */}
      {data.length > 0 && (
        <Box
          sx={{
            mt: 2,
            pt: 2,
            borderTop: '1px solid rgba(0, 0, 0, 0.08)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem' }}>
            Total Top 5
          </Typography>
          <Typography variant="subtitle2" sx={{ color: '#1A237E', fontWeight: 600 }}>
            {formatCurrency(
              data.reduce((sum, t) => sum + parseFloat(t.comisiones_totales || 0), 0),
              currency
            )}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default TopTrabajadoresRanking;
